import { z } from "zod";
import { getIdSchema, ID, makeIdGenerator } from "../utils";
import { accountIdSchema } from "./accountSchema";
import { userIdSchema } from "./userSchema";

export const clientLogIdPrefix = "clg_";
export type ClientLogId = ID<typeof clientLogIdPrefix>;
export const generateClientLogId = makeIdGenerator(clientLogIdPrefix);
export const clientLogIdSchema = getIdSchema(clientLogIdPrefix, "ClientLogId");

// Mirrors the Prisma `ClientLogLevel` enum. Ordered least to most severe;
// the debug API's `level` filter means "this level and above".
export const clientLogLevelSchema = z.enum(["debug", "info", "warn", "error"]);
export type ClientLogLevel = z.infer<typeof clientLogLevelSchema>;
export const CLIENT_LOG_LEVELS = clientLogLevelSchema.options;

/** Levels at or above `min`, for the debug API's severity filter. */
export function clientLogLevelsFrom(
  min: ClientLogLevel,
): readonly ClientLogLevel[] {
  return CLIENT_LOG_LEVELS.slice(CLIENT_LOG_LEVELS.indexOf(min));
}

// Frontend-visible size caps. The client truncates oversize `data` before
// sending (see features/telemetry); the server refuses anything larger so
// a bug can't fill the table with multi-megabyte rows.
export const CLIENT_LOG_MAX_MESSAGE_LENGTH = 4000;
export const CLIENT_LOG_MAX_DATA_JSON_LENGTH = 32_000;
export const CLIENT_LOG_MAX_BATCH_SIZE = 200;

const jsonDataSchema = z
  .unknown()
  .optional()
  .refine(
    (data) =>
      data === undefined ||
      JSON.stringify(data).length <= CLIENT_LOG_MAX_DATA_JSON_LENGTH,
    {
      message: `data must serialise to at most ${CLIENT_LOG_MAX_DATA_JSON_LENGTH} characters`,
    },
  );

/**
 * One entry as the frontend posts it. Identity (account/user) is never in
 * the body — the server stamps it from the session.
 *
 * The client mints `id` when it queues the entry, and it is the stored
 * row's primary key, so re-posting an entry is a no-op. It has to be:
 * the ingest route commits the rows *before* it replies, so a batch whose
 * response is lost (a navigation aborting the in-flight fetch, say) is
 * re-queued and sent again even though it already landed — which is how
 * one 58-entry batch got stored twice (docs/client-telemetry.md,
 * "Duplicate delivery"). Optional only so a tab still running the previous
 * bundle across a deploy keeps reporting; the server mints one for those.
 */
export const clientLogEntrySchema = z.object({
  id: clientLogIdSchema.optional(),
  time: z.coerce.date(),
  level: clientLogLevelSchema,
  message: z.string().min(1).max(CLIENT_LOG_MAX_MESSAGE_LENGTH),
  data: jsonDataSchema,
  path: z.string().max(2000).optional(),
});

export type ClientLogEntry = z.infer<typeof clientLogEntrySchema>;

/** Request body for POST /api/client-logs. */
export const postClientLogsBodySchema = z.object({
  sessionId: z.string().min(1).max(64),
  /** The client's clock when the batch was sent — lets the server re-anchor entry times to its own clock. */
  sentAt: z.coerce.date(),
  entries: z.array(clientLogEntrySchema).min(1).max(CLIENT_LOG_MAX_BATCH_SIZE),
});

export type PostClientLogsBody = z.infer<typeof postClientLogsBodySchema>;

/** A stored entry, as returned by the debug API. */
export const clientLogSchema = z
  .object({
    id: clientLogIdSchema,
    accountId: accountIdSchema,
    userId: userIdSchema,
    actorUserId: userIdSchema.nullable(),
    sessionId: z.string(),
    time: z.coerce.date(),
    clientTime: z.coerce.date(),
    receivedAt: z.coerce.date(),
    level: clientLogLevelSchema,
    message: z.string(),
    data: z.unknown().nullable(),
    path: z.string().nullable(),
    userAgent: z.string().nullable(),
  })
  .meta({ id: "ClientLog" });

export type ClientLog = z.infer<typeof clientLogSchema>;

/** One browser-tab session's worth of entries, summarised (debug API). */
export const clientLogSessionSchema = z
  .object({
    sessionId: z.string(),
    userId: userIdSchema,
    accountId: accountIdSchema,
    count: z.number().int(),
    firstTime: z.coerce.date(),
    lastTime: z.coerce.date(),
  })
  .meta({ id: "ClientLogSession" });

export type ClientLogSession = z.infer<typeof clientLogSessionSchema>;
